import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { getPending, removePending, countPending, cacheGarments, updateLocalSale } from '@/lib/offlineDb';
import { applyThemeConfig, cacheTheme, cacheBranding, cacheSplash, cacheLogin, applyBrandMeta, normaliseTheme, defaultThemeConfig, readMode, saveMode, applyMode } from '@/lib/theme';
import { applyAppIcon } from '@/lib/appIcon';

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [stores, setStores] = useState([]);
  const [currentStore, setCurrentStore] = useState(localStorage.getItem('sc_store') || 'store-1');
  const [online, setOnline] = useState(navigator.onLine);
  const [syncing, setSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [booting, setBooting] = useState(true);
  const [settings, setSettings] = useState(null);
  const [mode, setMode] = useState(readMode());
  const [hideCharts, setHideCharts] = useState(localStorage.getItem('sc_hidecharts') === '1');
  const [compact, setCompact] = useState(localStorage.getItem('sc_compact') === '1');
  const syncRef = useRef(false);

  // Apply per-device appearance prefs once on mount (before/independent of login).
  useEffect(() => {
    applyMode(readMode());
    document.documentElement.setAttribute('data-density', compact ? 'compact' : 'comfortable');
    document.documentElement.setAttribute('data-hide-charts', hideCharts ? '1' : '0');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleMode = useCallback(() => {
    setMode((m) => {
      const next = m === 'light' ? 'dark' : 'light';
      saveMode(next); applyMode(next);
      return next;
    });
  }, []);
  const toggleHideCharts = useCallback(() => {
    setHideCharts((v) => {
      const next = !v;
      localStorage.setItem('sc_hidecharts', next ? '1' : '0');
      document.documentElement.setAttribute('data-hide-charts', next ? '1' : '0');
      return next;
    });
  }, []);
  const toggleCompact = useCallback(() => {
    setCompact((v) => {
      const next = !v;
      localStorage.setItem('sc_compact', next ? '1' : '0');
      document.documentElement.setAttribute('data-density', next ? 'compact' : 'comfortable');
      return next;
    });
  }, []);

  // Backward-compatible: accepts a full theme config object OR a legacy
  // primary HSL-triple string. Presentation only.
  const applyTheme = useCallback((arg) => {
    if (!arg) return;
    if (typeof arg === 'object' && arg.tokens) {
      applyThemeConfig(arg);
      cacheTheme(arg);
      return;
    }
    // legacy: only the accent/primary was provided
    document.documentElement.style.setProperty('--primary', arg);
    document.documentElement.style.setProperty('--ring', arg);
    document.documentElement.style.setProperty('--chart-1', arg);
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const r = await api.get('/settings');
      setSettings(r.data.data);
      cacheBranding(r.data.data?.branding || {});
      cacheSplash(r.data.data?.splash || {});
      cacheLogin(r.data.data?.login || {});
      applyBrandMeta(r.data.data?.branding || {});
      // Use the uploaded brand logo as the installed app / home-screen icon,
      // composited onto a light background so it never renders as a black square.
      try {
        const logo = r.data.data?.branding?.logo;
        if (logo) applyAppIcon(logo);
      } catch (e) { /* ignore */ }
      const full = normaliseTheme(r.data.data?.theme);
      if (full) { applyThemeConfig(full); cacheTheme(full); }
      else {
        // No full theme saved yet -> adopt the premium SHABZ default look.
        const def = defaultThemeConfig();
        applyThemeConfig(def); cacheTheme(def);
      }
      // Re-apply the per-device Light/Dark choice on top of the shared theme.
      applyMode(readMode());
      return r.data.data;
    } catch (e) { return null; }
  }, []);

  const refreshPending = useCallback(async () => {
    setPendingCount(await countPending());
  }, []);

  const loadStores = useCallback(async () => {
    try {
      const r = await api.get('/stores');
      setStores(r.data.data);
    } catch (e) {}
  }, []);

  const cacheStore = useCallback(async (storeId) => {
    try {
      const r = await api.get('/sync/pull', { params: { store_id: storeId } });
      await cacheGarments(r.data.data.garments);
    } catch (e) {}
  }, []);

  const flushPending = useCallback(async () => {
    if (syncRef.current || !navigator.onLine) return;
    const ops = await getPending();
    if (!ops.length) { await refreshPending(); return; }
    syncRef.current = true;
    setSyncing(true);
    try {
      const r = await api.post('/sync/push', { operations: ops });
      const results = r.data.data.results || [];
      for (const res of results) {
        if (res.status === 'ok') {
          await updateLocalSale(res.client_txn_id, {
            synced: true, invoice_no: res.result.invoice_no, conflict: false });
          await removePending(res.client_txn_id);
        } else if (res.status === 'conflict') {
          await updateLocalSale(res.client_txn_id, { conflict: true, error: res.error });
          await removePending(res.client_txn_id);
        }
      }
    } catch (e) {
      // stay queued, retry later
    } finally {
      syncRef.current = false;
      setSyncing(false);
      await refreshPending();
    }
  }, [refreshPending]);

  // boot: restore session
  useEffect(() => {
    (async () => {
      const token = localStorage.getItem('sc_token');
      if (token) {
        try {
          const r = await api.get('/auth/me');
          setUser(r.data.data);
          await loadStores();
          await loadSettings();
        } catch (e) {}
      }
      await refreshPending();
      setBooting(false);
    })();
  }, [loadStores, refreshPending]);

  // online/offline listeners + periodic sync
  useEffect(() => {
    const goOnline = () => { setOnline(true); flushPending(); };
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    const iv = setInterval(() => { if (navigator.onLine) flushPending(); }, 15000);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      clearInterval(iv);
    };
  }, [flushPending]);

  // Refresh branding/settings when the app regains focus so logo/branding
  // changes made elsewhere show up automatically for open (installed) apps.
  useEffect(() => {
    const onFocus = () => { if (document.visibilityState !== 'hidden' && navigator.onLine) loadSettings(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [loadSettings]);

  // cache store stock when user + store known
  useEffect(() => {
    if (user && currentStore && navigator.onLine) cacheStore(currentStore);
  }, [user, currentStore, cacheStore]);

  const login = async (username, password) => {
    const r = await api.post('/auth/login', { username, password });
    return _establishSession(r.data.data.token, r.data.data.user);
  };

  // Shared session bootstrap used by password login AND biometric Quick Login.
  const _establishSession = async (token, fallbackUser) => {
    localStorage.setItem('sc_token', token);
    let me = fallbackUser;
    try { me = (await api.get('/auth/me')).data.data; } catch (e) { /* use fallback */ }
    setUser(me);
    const perUser = localStorage.getItem('sc_store_' + me.username);
    const targetStore = perUser || me.store_id;
    if (targetStore) {
      setCurrentStore(targetStore);
      localStorage.setItem('sc_store', targetStore);
      localStorage.setItem('sc_store_' + me.username, targetStore);
    }
    await loadStores();
    await loadSettings();
    return me;
  };

  // Quick Login establishes the SAME secure JWT session from a backend-verified
  // WebAuthn assertion (token already minted server-side). No weaker path.
  const loginWithToken = async (token, userObj) => _establishSession(token, userObj);

  const can = useCallback((perm) => {
    if (!user) return false;
    if (user.owner_access || user.role === 'OWNER') return true;
    return Array.isArray(user.permissions) && user.permissions.includes(perm);
  }, [user]);

  const logout = () => {
    localStorage.removeItem('sc_token');
    setUser(null);
  };

  const selectStore = (id) => {
    setCurrentStore(id);
    localStorage.setItem('sc_store', id);
    if (user?.username) localStorage.setItem('sc_store_' + user.username, id);
  };

  const syncStatus = !online ? 'offline' : (syncing || pendingCount > 0 ? 'syncing' : 'synced');

  return (
    <AppContext.Provider value={{
      user, stores, currentStore, online, syncing, pendingCount, booting, syncStatus,
      login, logout, selectStore, loadStores, flushPending, refreshPending, cacheStore,
      setUser, settings, loadSettings, applyTheme, can, loginWithToken,
      mode, toggleMode, hideCharts, toggleHideCharts, compact, toggleCompact,
    }}>
      {children}
    </AppContext.Provider>
  );
}
